/**
 * @module features/devices/components/TelemetryLiveLogStream
 * @description Live console of the retained telemetry logs (<= 30, newest first):
 * Live/Paused toggle, search filter, desktop table vs mobile stacked cards, and an
 * expandable inspector showing the raw JSON and the originating `request_id`.
 */
import * as React from "react";
import { ChevronDown, ChevronRight, Pause, Play, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { DisplayModuleTelemetryLog } from "../types/telemetry";
import { formatDateTimeSeconds, formatUtc } from "../utils/format";
import { StreamState } from "../api/telemetry-api";
import { LOG_PANEL_HEIGHT } from "./TelemetryDashboardSkeleton";

interface TelemetryLiveLogStreamProps {
  logs: DisplayModuleTelemetryLog[];
  live: boolean;
  streamState: StreamState;
  onToggleLive: () => void;
}

const STATE_LABEL: Record<StreamState, string> = {
  connecting: "Connecting…",
  live: "Live",
  polling: "Polling (5s)",
  paused: "Paused",
};

export function matchesFilter(log: DisplayModuleTelemetryLog, term: string): boolean {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  return [log.request_id, log.connected_wifi_name, log.wifi_strength, log.os_hash, log.mac_id, log.id]
    .join(" ")
    .toLowerCase()
    .includes(t);
}

function Inspector({ log }: { log: DisplayModuleTelemetryLog }) {
  return (
    <div className="rounded-lg border border-border/80 bg-muted/30 p-3 text-[11px]">
      <p className="mb-2 font-mono break-all">
        <span className="text-muted-foreground">request_id:</span> {log.request_id}
      </p>
      <pre className="overflow-x-auto font-mono leading-relaxed">{JSON.stringify(log, null, 2)}</pre>
    </div>
  );
}

export function TelemetryLiveLogStream({ logs, live, streamState, onToggleLive }: TelemetryLiveLogStreamProps) {
  const [term, setTerm] = React.useState("");
  const [openId, setOpenId] = React.useState<string | null>(null);
  const filtered = React.useMemo(() => logs.filter((l) => matchesFilter(l, term)), [logs, term]);

  return (
    <section
      aria-label="Live telemetry logs"
      className="rounded-xl border border-border/80 bg-card shadow-xs flex flex-col"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 p-3">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Live logs <span className="font-normal normal-case">({logs.length}/30)</span>
          </h3>
          <span
            role="status"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
              streamState === "live"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/60"
                : "bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700"
            )}
          >
            <span
              aria-hidden="true"
              className={cn("h-1.5 w-1.5 rounded-full", streamState === "live" ? "bg-emerald-500 animate-pulse" : "bg-zinc-400")}
            />
            {STATE_LABEL[streamState]}
          </span>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Filter logs"
              aria-label="Filter logs"
              className="h-8 pl-8 text-xs bg-card"
            />
          </div>
          <Button type="button" variant="outline" size="sm" onClick={onToggleLive} className="h-8 gap-1.5 text-xs">
            {live ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            {live ? "Pause" : "Resume"}
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className={cn("grid place-items-center text-xs text-muted-foreground", "h-32")}>
          No logs match your filter.
        </p>
      ) : (
        <>
          {/* Desktop / tablet table */}
          <div className={cn("hidden sm:block overflow-auto", LOG_PANEL_HEIGHT)}>
            <table className="w-full text-left text-[11px]">
              <thead className="sticky top-0 bg-card text-muted-foreground">
                <tr className="border-b border-border/60">
                  <th className="w-8 px-3 py-2" aria-label="Expand" />
                  <th className="px-3 py-2 font-medium">Received</th>
                  <th className="px-3 py-2 font-medium">CPU</th>
                  <th className="px-3 py-2 font-medium">Memory</th>
                  <th className="px-3 py-2 font-medium">WiFi</th>
                  <th className="hidden lg:table-cell px-3 py-2 font-medium">Request ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((log) => {
                  const open = openId === log.id;
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className="border-b border-border/40 hover:bg-muted/20 cursor-pointer"
                        onClick={() => setOpenId(open ? null : log.id)}
                      >
                        <td className="px-3 py-1.5">
                          <button
                            type="button"
                            aria-expanded={open}
                            aria-label={open ? "Collapse log details" : "Expand log details"}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenId(open ? null : log.id);
                            }}
                          >
                            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </button>
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap" title={formatUtc(log.received_timestamp)}>
                          {formatDateTimeSeconds(log.received_timestamp)}
                        </td>
                        <td className="px-3 py-1.5 font-mono">{log.cpu_load.toFixed(1)}%</td>
                        <td className="px-3 py-1.5 font-mono">{log.memory_load_percentage.toFixed(1)}%</td>
                        <td className="px-3 py-1.5">
                          <span className="capitalize">{log.wifi_strength}</span>{" "}
                          <span className="font-mono text-muted-foreground">{log.wifi_rssi}</span>
                        </td>
                        <td className="hidden lg:table-cell px-3 py-1.5 font-mono text-muted-foreground max-w-[200px] truncate">
                          {log.request_id}
                        </td>
                      </tr>
                      {open && (
                        <tr className="border-b border-border/40">
                          <td colSpan={6} className="px-3 pb-3">
                            <Inspector log={log} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked cards */}
          <ul className="sm:hidden space-y-2 p-3">
            {filtered.map((log) => {
              const open = openId === log.id;
              return (
                <li key={log.id} className="rounded-lg border border-border/80 p-3">
                  <button
                    type="button"
                    className="w-full text-left"
                    aria-expanded={open}
                    onClick={() => setOpenId(open ? null : log.id)}
                  >
                    <p className="text-[11px] font-medium" title={formatUtc(log.received_timestamp)}>
                      {formatDateTimeSeconds(log.received_timestamp)}
                    </p>
                    <p className="mt-1 text-[11px] text-muted-foreground font-mono">
                      CPU {log.cpu_load.toFixed(1)}% · Mem {log.memory_load_percentage.toFixed(1)}% · {log.wifi_strength}{" "}
                      {log.wifi_rssi}
                    </p>
                  </button>
                  {open && (
                    <div className="mt-2">
                      <Inspector log={log} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

